import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import classNames from 'classnames';
import Lottie from 'lottie-react';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { useTickets } from './hooks/useTickets';
import { Ticket } from './types/ticket';
import confettiAnimation from './assets/Confetti.json';
import GoogleSheetsService from './services/googleSheets';
import SuccessPopup from './components/SuccessPopup';

// Fix for zaloJSV2 error
if (typeof window !== 'undefined') {
  (window as any).zaloJSV2 = (window as any).zaloJSV2 || {};
}

function App() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [foundTickets, setFoundTickets] = useState<Ticket[]>([]);
  const [showTicketList, setShowTicketList] = useState(false);
  const [checkingInTickets, setCheckingInTickets] = useState<Set<string>>(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successTicket, setSuccessTicket] = useState<Ticket | null>(null);
  const [checkInCount, setCheckInCount] = useState(1);
  const [queueStatus, setQueueStatus] = useState<{
    isProcessing: boolean;
    queueSize: number;
    activeRequests: number;
  }>({
    isProcessing: false,
    queueSize: 0,
    activeRequests: 0
  });
  const { getTicketsByPhone, getTicketsByEmail } = useTickets();
  const appClasses = classNames('App', {
    // Có thể thêm các class conditional ở đây
  });

  // Initialize zaloJSV2 to prevent runtime errors
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).zaloJSV2 = (window as any).zaloJSV2 || {};
    }
  }, []);

  // Function to validate if input is email
  const isEmail = (input: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input.trim());
  };

  // Function to validate if input is phone number (simplified)
  const isPhoneNumber = (input: string): boolean => {
    const cleanInput = input.replace(/\D/g, '');
    // Vietnamese phone numbers: 8-11 digits (more flexible)
    return cleanInput.length >= 8 && cleanInput.length <= 11;
  };

  const normalizeTicketTier = (hangVe: string): string => {
    if (hangVe?.toLowerCase().includes('vé tặng')) {
      return 'BASIC';
    }
    return hangVe;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
     if (!phoneNumber.trim()) {
      toast.error('Vui lòng nhập số điện thoại hoặc email!');
      return;
    }

    const input = phoneNumber.trim();
    
    // Validate input - if not email, treat as phone number
    if (!isEmail(input) && !isPhoneNumber(input)) {
      toast.error('Vui lòng nhập đúng định dạng số điện thoại hoặc email!');
      return;
    }

    setIsLoading(true);
    setShowTicketList(false);
    setFoundTickets([]);
    
    try {
      let tickets: Ticket[] = [];
      
      if (isEmail(input)) {
        tickets = await getTicketsByEmail(input);
        if (tickets.length === 0) {
          toast.error('Không tìm thấy vé đã thanh toán với email này. Vui lòng kiểm tra lại thông tin!');
          return;
        }
      } else {
        tickets = await getTicketsByPhone(input);
        if (tickets.length === 0) {
          toast.error('Không tìm thấy vé đã thanh toán với sđt này. Vui lòng kiểm tra lại thông tin!');
          return;
        }
      }

      setFoundTickets(tickets);
      setShowTicketList(true);
      toast.success(`Tìm thấy ${tickets.length} vé đã thanh toán!`);
      
    } catch (error) {
      toast.error('Có lỗi xảy ra khi tìm kiếm vé!');
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async (ticket: Ticket) => {
    try {
      // Add ticket to checking in set
      setCheckingInTickets(prev => new Set(Array.from(prev).concat(ticket.maCheckin)));
      
      // Show queue status
      setQueueStatus(prev => ({ ...prev, isProcessing: true, queueSize: prev.queueSize + 1 }));
      
      // Show initial loading toast
      const loadingToastId = toast.loading(`Đang xử lý check-in cho ${ticket.hoTen}...`, {
        position: "top-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
      
      const checkInData = {
        maCheckin: ticket.maCheckin,
        hoTen: ticket.hoTen,
        sdt: ticket.sdt,
        email: ticket.email,
        hangVe: normalizeTicketTier(ticket.hangVe || ''),
        checkedInTime: new Date().toLocaleString('vi-VN'),
        timestamp: new Date().toISOString(),
      };

      const success = await GoogleSheetsService.getInstance().submitCheckIn(checkInData);
      
      // Dismiss loading toast
      toast.dismiss(loadingToastId);
      
      if (success) {
        // Update ticket status locally
        setFoundTickets(prev => 
          prev.map(t => 
            t.maCheckin === ticket.maCheckin 
              ? { ...t, checkedIn: true, checkedInTime: checkInData.checkedInTime }
              : t
          )
        );
        
        // Show success popup
        setSuccessTicket(ticket);
        setCheckInCount(1); // You can modify this logic based on your requirements
        setShowSuccessPopup(true);
        
        // Also show confetti animation for extra celebration
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000); // Hide after 3 seconds
      } else {
        throw new Error('Check-in không thành công');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Có lỗi xảy ra khi check-in!';
      
      if (error instanceof Error) {
        if (error.message.includes('duplicate') || error.message.includes('đã được sử dụng')) {
          errorMessage = 'Mã check-in này đã được sử dụng trước đó!';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = 'Lỗi kết nối mạng. Vui lòng kiểm tra internet và thử lại!';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Hết thời gian chờ. Vui lòng thử lại!';
        } else if (error.message.includes('quota') || error.message.includes('limit')) {
          errorMessage = 'Hệ thống đang quá tải. Vui lòng thử lại sau vài giây!';
        } else {
          errorMessage = `Lỗi: ${error.message}`;
        }
      }
      
      toast.error(`❌ ${errorMessage}`, {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      // Remove ticket from checking in set
      setCheckingInTickets(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticket.maCheckin);
        return newSet;
      });
      
      // Update queue status
      setQueueStatus(prev => ({ 
        ...prev, 
        queueSize: Math.max(0, prev.queueSize - 1),
        isProcessing: prev.queueSize > 1
      }));
    }
  };

  const handleBackToSearch = () => {
    setShowTicketList(false);
    setFoundTickets([]);
    setPhoneNumber('');
    setCheckingInTickets(new Set());
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessTicket(null);
  };

  return (
    <Router>
      <div className={appClasses}>
        {/* Confetti Animation */}
        {showConfetti && (
          <div className="confetti-overlay">
            <Lottie 
              animationData={confettiAnimation}
              loop={false}
              autoplay={true}
              style={{ 
                width: '100%', 
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 99999
              }}
            />
            {/* Fallback CSS confetti for mobile */}
            <div className="css-confetti">
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
              <div className="confetti-piece"></div>
            </div>
          </div>
        )}
        {/* Logo Section */}
        <div className="logo">
          <div className="logo-placeholder">
            <div className="logo-main">
              <img src="/assets/logos.png" alt="TUKI GROUP" />
            </div>
          </div>
        </div>

        {/* Event Info */}
        <div className="event-info">
          <img 
            src="/assets/AiEcommerce.png" 
            alt="AI E-COMMERCE REVOLUTION" 
            className="event-title-image" 
          />
        </div>

        {/* Check-in Container */}
        <div className="checkin-container">
          {!showTicketList ? (
            <>
              <h4 className="checkin-title">Check-in sự kiện</h4>
              <form className="checkin-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  className="checkin-input"
                  placeholder="Nhập sđt hoặc email"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <button type="submit" className="checkin-btn" disabled={isLoading}>
                  {isLoading ? 'Đang tìm vé...' : 'TÌM KIẾM VÉ'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h4 className="checkin-title">Danh sách vé đăng ký</h4>
              <div className="ticket-list">
                <div className="ticket-info-header">
                <span>{isEmail(phoneNumber) ? 'Email' : 'SĐT'}: {phoneNumber}</span>
                  <button 
                    type="button" 
                    className="back-btn" 
                    onClick={handleBackToSearch}
                    disabled={isLoading}
                  >
                    ←
                  </button>
                </div>
                
                {/* Queue Status */}
                {queueStatus.isProcessing && (
                  <div className="queue-status">
                    <div className="queue-indicator">
                      <div className="spinner"></div>
                      <span>Đang xử lý {queueStatus.queueSize} yêu cầu check-in...</span>
                    </div>
                  </div>
                )}

                {/* Ticket Summary */}
                <div className="ticket-summary">
                  <div className="ticket-summary-title">
                    Tìm thấy {foundTickets.length} vé đã thanh toán!
                  </div>
                  <div className="ticket-summary-details">
                    {(() => {
                      const ticketCounts = foundTickets.reduce((acc, ticket) => {
                        const normalizedTier = normalizeTicketTier(ticket.hangVe || '');
                        const tier = normalizedTier.toLowerCase();
                        acc[tier] = (acc[tier] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                      
                      return Object.entries(ticketCounts).map(([tier, count]) => (
                        <span key={tier} className={`ticket-count ${tier}`}>
                          {count} vé {tier === 'diamond' ? 'Diamond' : tier === 'gold' ? 'Gold' : tier === 'silver' ? 'Silver' : tier === 'basic' ? 'Basic' : tier}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
                
                <div className="ticket-list-scrollable">
                  {foundTickets.length === 0 ? (
                    <div className="no-tickets">
                      <p>Không tìm thấy vé nào cho {isEmail(phoneNumber) ? 'email' : 'số điện thoại'} này.</p>
                    </div>
                  ) : (
                    foundTickets.map((ticket) => (
                  <div key={ticket.maCheckin} className={`ticket-item ${normalizeTicketTier(ticket.hangVe || '').toLowerCase()}`}>
                    <div className="ticket-content">
                      <div className="ticket-header">
                        <div className="ticket-name">{ticket.hoTen}</div>
                        <div className={`ticket-status ${ticket.checkedIn ? 'checked-in' : 'not-checked'}`}>
                          {ticket.checkedIn ? '✓ Đã check-in' : 'Chưa check-in'}
                        </div>
                      </div>
                      
                      <div className="ticket-details">
                        {ticket.hangVe && (
                          <div className="ticket-detail">
                            <span className="label">Hạng vé:</span>
                            <span className={`value ticket-tier ${normalizeTicketTier(ticket.hangVe).toLowerCase()}`}>
                              {normalizeTicketTier(ticket.hangVe)}
                            </span>
                          </div>
                        )}
                        {ticket.maCheckin && (
                          <div className="ticket-detail">
                            <span className="label">Mã vé:</span>
                            <span className="value ticket-code">{ticket.maCheckin}</span>
                          </div>
                        )}
                        {ticket.email && (
                          <div className="ticket-detail">
                            <span className="label">Email:</span>
                            <span className="value">{ticket.email}</span>
                          </div>
                        )}
                        {ticket.checkedIn && ticket.checkedInTime && (
                          <div className="ticket-detail">
                            <span className="label">Thời gian check-in:</span>
                            <span className="value checkin-time">{ticket.checkedInTime}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {!ticket.checkedIn && (
                      <button
                        type="button"
                        className="checkin-ticket-btn"
                        onClick={() => handleCheckIn(ticket)}
                        disabled={checkingInTickets.has(ticket.maCheckin) || queueStatus.isProcessing}
                      >
                        {checkingInTickets.has(ticket.maCheckin) 
                          ? 'Đang check-in...' 
                          : queueStatus.isProcessing 
                            ? 'Đang xử lý...' 
                            : 'CHECK-IN'
                        }
                      </button>
                    )}
                  </div>
                  ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Robot Image Section */}
        <div className="robot-image-section">
          <img 
            src="/assets/RobotEcommerce.png" 
            alt="Robot AI" 
            className="robot-image" 
          />
        </div>

        {/* Event Details */}
        <div className="event-details">
          <div className="event-date">
            <div className="date-main">17.09.2025</div>
          </div>
          <div className="event-separator"></div>
          <div className="event-day-time">
            <div className="day-of-week">Thứ Tư</div>
            <div className="time-range">7h30 - 17h30</div>
          </div>
          <div className="event-separator"></div>
          <div className="event-location">
            <div className="location-main">Trống Đồng Palace</div>
            <div className="location-address">
              489 Hoàng Quốc Việt, p.Nghĩa Đô, Hà Nội
            </div>
          </div>
        </div>

        {/* Success Popup */}
        {showSuccessPopup && successTicket && (
          <SuccessPopup
            ticket={successTicket}
            onClose={handleCloseSuccessPopup}
            checkInTime={new Date().toLocaleString('vi-VN')}
            checkInCount={checkInCount}
          />
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          toastStyle={{
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)',
            fontSize: '14px',
          }}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            left: 'auto',
            bottom: 'auto',
            zIndex: 10000,
            transform: 'none',
          }}
        />
    </div>
    </Router>
  );
}

export default App;
